// src/components/public_utilities/AssetMarkers.js
// --- UPDATE (Public Utilities v3)
//   • Performance: every marker logged on every render (console.log + a synchronous sessionStorage
//     parse/stringify per marker — ~1,800 of each per map render with the imported inventory).
//     Console output is development-only now, storage is batched, per-marker logs are dev-only.
//   • Linked-sensor lookup is a Map built once per render (was sensors.find() for every asset).
//   • Icons + colours for the Public Utilities types and statuses (drainage well, catch basin,
//     building, sidewalk · failed, damaged, under maintenance, maintenance required, closed, planned).
//   • Popup shows the asset code (DW-BAM-0001) and constituency when present.
//   Props and defaults are unchanged.
// --- END UPDATE
import React, { useMemo, useCallback, memo } from 'react';
// 🚀 UPDATE: Added Polyline to the react-leaflet imports
import { Marker, Popup, Tooltip, Polyline } from 'react-leaflet';
import L from 'leaflet';
import PropTypes from 'prop-types';

// ============================================================================
// CSS KEYFRAMES FOR CASCADING ALERTS
// ============================================================================
const cascadingAnimation = `
  @keyframes criticalAssetPulse {
    0% { box-shadow: 0 0 0 0 rgba(244, 67, 54, 0.8); }
    70% { box-shadow: 0 0 0 20px rgba(244, 67, 54, 0); }
    100% { box-shadow: 0 0 0 0 rgba(244, 67, 54, 0); }
  }
  @keyframes floodMarkerPulse {
    0% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(229, 62, 62, 0.85); }
    70% { transform: scale(1.15); box-shadow: 0 0 0 18px rgba(229, 62, 62, 0); }
    100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(229, 62, 62, 0); }
  }
  .asset-marker.pulse-critical {
    animation: criticalAssetPulse 1.5s infinite;
    z-index: 1000 !important;
  }
  .asset-marker.flood-pulsing-marker {
    animation: floodMarkerPulse 1.4s infinite !important;
    background-color: #E53E3E !important;
    border-color: #FFFFFF !important;
    z-index: 1100 !important;
  }
`;

// ============================================================================
// DEBUG UTILITY
// ============================================================================
// --- UPDATE (v3)
const MARKER_DEBUG = process.env.NODE_ENV === 'development' || (typeof window !== 'undefined' && window.__DEBUG__);
// --- END UPDATE

class AssetMarkersDebugger {
  constructor() {
    this.logs = [];
    this.maxLogs = 500;
    this.renderCount = 0;
  }

  log(level, action, data = {}) {
    const logEntry = {
      timestamp: new Date().toISOString(),
      level,
      action,
      component: 'AssetMarkers',
      ...data,
      renderCount: this.renderCount
    };

    this.logs.push(logEntry);
    if (this.logs.length > this.maxLogs) {
      this.logs.shift();
    }

    const colors = {
      INFO: '#2196F3',
      SUCCESS: '#4CAF50',
      WARNING: '#FF9800',
      ERROR: '#F44336',
      DEBUG: '#9C27B0',
      PERFORMANCE: '#00BCD4'
    };

    // --- UPDATE (v3): dev-only console, batched storage
    if (MARKER_DEBUG) {
      console.log(
        `%c[${level}] ${action}`,
        `color: ${colors[level]}; font-weight: bold;`,
        data
      );
    }
    if (this._flushTimer) return;
    this._flushTimer = setTimeout(() => {
      this._flushTimer = null;
      try { sessionStorage.setItem('asset_markers_logs', JSON.stringify(this.logs.slice(-100))); } catch (e) { /* storage full / private mode */ }
    }, 1000);
    // --- END UPDATE
  }

  incrementRender() {
    this.renderCount++;
  }

  getStats() {
    return {
      totalLogs: this.logs.length,
      renderCount: this.renderCount,
      recentLogs: this.logs.slice(-10)
    };
  }

  exportLogs() {
    const dataStr = JSON.stringify(this.logs, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `asset-markers-debug-${Date.now()}.json`;
    link.click();
    URL.revokeObjectURL(url);
  }

  clearLogs() {
    this.logs = [];
    sessionStorage.removeItem('asset_markers_logs');
    this.log('INFO', 'logs_cleared');
  }
}

const assetMarkerDebugger = new AssetMarkersDebugger();

if (typeof window !== 'undefined') {
  window.__AssetMarkersDebug__ = {
    getLogs: () => assetMarkerDebugger.logs,
    getStats: () => assetMarkerDebugger.getStats(),
    exportLogs: () => assetMarkerDebugger.exportLogs(),
    clearLogs: () => assetMarkerDebugger.clearLogs()
  };
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

const getAssetTypeIcon = (type) => {
  const icons = {
    'Street Light': 'lightbulb',
    'Traffic Light': 'traffic-light',
    'Water Pipe': 'tint',
    'Well': 'tint',
    'well': 'tint',
    'Power Line': 'bolt',
    'Bridge': 'road',
    'Building': 'building',
    'Pump': 'cog',
    'Valve': 'valve',
    'Hydrant': 'fire-extinguisher',
    'Transformer': 'plug',
    // --- UPDATE (v3): Public Utilities catalogue types
    'building': 'building', 'drainage_well': 'tint', 'catch_basin': 'water', 'Catch Basin': 'water',
    'sidewalk': 'walking', 'Sidewalk': 'walking', 'culvert': 'water', 'manhole': 'circle',
    'street_light': 'lightbulb', 'traffic_signal': 'traffic-light', 'pole': 'bolt', 'generator': 'charging-station',
    'pump': 'cog', 'lift_station': 'cogs', 'valve': 'sliders-h', 'hydrant': 'fire-extinguisher', 'meter': 'tachometer-alt', 'tank': 'database', 'sensor': 'satellite-dish',
    // --- END UPDATE
    'default': 'map-marker-alt'
  };
  return icons[type] || icons[String(type || '').toLowerCase()] || icons.default; // --- UPDATE (v3): case-insensitive
};

const getStatusColor = (status) => {
  const colors = {
    operational: '#4CAF50',
    warning: '#FF9800',
    critical: '#F44336',
    maintenance: '#2196F3',
    offline: '#9E9E9E',
    // --- UPDATE (v3): Public Utilities statuses
    failed: '#F44336', damaged: '#FF5722', maintenance_required: '#FFC107', under_maintenance: '#2196F3',
    closed: '#9E9E9E', planned: '#03A9F4', decommissioned: '#757575', unknown: '#9E9E9E',
    // --- END UPDATE
  };
  return colors[status?.toLowerCase()] || '#9E9E9E';
};

const getAlertColor = (severity) => {
  const colors = {
    low: '#4CAF50',
    medium: '#FF9800',
    high: '#F44336',
    critical: '#9C27B0'
  };
  return colors[severity?.toLowerCase()] || '#FF9800';
};

// ============================================================================
// CREATE LEAFLET ICON
// ============================================================================

const createAssetIcon = (asset, hasAlerts, isSelected, isCascadingCritical, isFloodCritical = false) => {
  let statusColor = getStatusColor(asset.status);
  
  // 🔴 Turn well marker red if water level is high or flood hazard detected
  if (isFloodCritical) {
    statusColor = '#E53E3E';
  } else if (isCascadingCritical) {
    statusColor = '#F44336';
  }

  const iconName = getAssetTypeIcon(asset.type);
  const alertBadge = (hasAlerts || isCascadingCritical || isFloodCritical) ? '<span class="alert-badge">!</span>' : '';
  const selectedClass = isSelected ? 'selected' : '';
  const pulseClass = isFloodCritical ? 'flood-pulsing-marker' : (isCascadingCritical ? 'pulse-critical' : '');
  
  // 🚀 PHASE 4: Custom Bridge Icon Styling (Square with bridge icon)
  if (asset.type === 'Bridge') {
    const bridgeHtml = `
      <div class="asset-marker ${selectedClass} ${pulseClass}" style="width:36px;height:36px;background-color:${statusColor};border-radius:8px;border:3px solid white;display:flex;align-items:center;justify-content:center;box-shadow:0 3px 8px rgba(0,0,0,0.4);">
        <i class="fas fa-bridge" style="color: white; font-size: 16px;"></i>
        ${alertBadge}
      </div>
    `;
    return L.divIcon({
      className: 'custom-asset-icon',
      html: bridgeHtml,
      iconSize: [36, 36],
      iconAnchor: [18, 36],
      popupAnchor: [0, -36]
    });
  }

  const iconHtml = `
    <div class="asset-marker ${selectedClass} ${pulseClass}" style="background-color: ${statusColor}; border: 3px solid ${statusColor};">
      <i class="fas fa-${iconName}" style="color: white;"></i>
      ${alertBadge}
    </div>
  `;

  return L.divIcon({
    className: 'custom-asset-icon',
    html: iconHtml,
    iconSize: [40, 40],
    iconAnchor: [20, 20],
    popupAnchor: [0, -20]
  });
};

// ============================================================================
// 🚀 NEW COMPONENT: ROAD ASSET POLYLINE
// ============================================================================
const RoadAssetPolyline = memo(({ 
  asset, 
  isSelected, 
  alerts = [],
  onAssetClick,
  showTooltip = true,
  linkedSensor,
  sensorReading
}) => {
  // 🚀 FIX: Moved Hook to the top level (before early returns) to satisfy Rules of Hooks
  const handleClick = useCallback(() => {
    if (onAssetClick) onAssetClick(asset);
  }, [asset, onAssetClick]);

  const hasAlerts = alerts.length > 0;
  let isCascadingCritical = false;
  let criticalPsiValue = null;

  if (sensorReading) {
    const psi = sensorReading.psi ?? sensorReading.pressure ?? sensorReading.level;
    if (psi !== undefined) {
      if (typeof psi === 'number' && (psi > 100 || psi < 20)) {
        isCascadingCritical = true;
        criticalPsiValue = psi;
      } else if (psi === 'EMPTY' || psi === 'HIGH') {
        isCascadingCritical = true;
        criticalPsiValue = psi;
      }
    }
  }

  // 🚀 UPDATE: Added fallback parsing for the explicitly named `polyline_points` column we save in the controller.
  let polyData = asset.polyline_points || asset.path_coordinates || asset.geo_point;
  
  if (typeof polyData === 'string') {
      try { polyData = JSON.parse(polyData); } catch (e) { polyData = null; }
  }
  
  let positions = [];
  if (Array.isArray(polyData)) {
      positions = polyData
          .filter(p => p && typeof p.lat === 'number' && typeof p.lng === 'number')
          .map(p => [p.lat, p.lng]);
  } else if (polyData && polyData.type === 'LineString' && Array.isArray(polyData.coordinates)) {
      positions = polyData.coordinates.map(coord => [coord[1], coord[0]]);
  } else if (asset.start_lat && asset.start_lng && asset.end_lat && asset.end_lng) {
      positions = [
          [Number(asset.start_lat), Number(asset.start_lng)],
          [Number(asset.end_lat), Number(asset.end_lng)]
      ];
  }

  // Early return is now safe because Hooks were declared above
  if (positions.length < 2) return null;

  let statusColor = getStatusColor(asset.status);
  if (isCascadingCritical) {
    statusColor = '#F44336';
  }

  const weight = isSelected ? 8 : 5;
  const opacity = isSelected ? 1 : 0.8;

  return (
    <Polyline 
      positions={positions}
      pathOptions={{ color: statusColor, weight, opacity, dashArray: isCascadingCritical ? '10, 10' : undefined }}
      eventHandlers={{ click: handleClick }}
    >
      {showTooltip && (
        <Tooltip sticky direction="top" opacity={0.9}>
          <div className="asset-tooltip">
            <strong>{asset.name}</strong>
            <div className="asset-type">{asset.type}</div>
          </div>
        </Tooltip>
      )}

      <Popup maxWidth={300} minWidth={200}>
        <style>{cascadingAnimation}</style>
        <div className="asset-popup">
          <h4>{asset.name}</h4>
          
          <div className="asset-details">
            <div className="detail-row">
              <span className="label">Type:</span>
              <span className="value">{asset.type}</span>
            </div>
            
            <div className="detail-row">
              <span className="label">Status:</span>
              <span className="value" style={{ color: getStatusColor(asset.status) }}>
                <i className="fas fa-circle"></i> {asset.status || 'Unknown'}
              </span>
            </div>
            
            {asset.location && (
              <div className="detail-row">
                <span className="label">Location:</span>
                <span className="value">{asset.location}</span>
              </div>
            )}
            
            {asset.installDate && (
              <div className="detail-row">
                <span className="label">Installed:</span>
                <span className="value">
                  {new Date(asset.installDate).toLocaleDateString()}
                </span>
              </div>
            )}
            
            {asset.lastMaintenance && (
              <div className="detail-row">
                <span className="label">Last Maintenance:</span>
                <span className="value">
                  {new Date(asset.lastMaintenance).toLocaleDateString()}
                </span>
              </div>
            )}
          </div>

          {isCascadingCritical && (
            <div className="asset-alerts" style={{ marginTop: '15px' }}>
              <h5 style={{ color: '#F44336', fontWeight: 'bold' }}>
                <i className="fas fa-satellite-dish" style={{ marginRight: '5px' }}></i> 
                Sensor Anomaly Detected
              </h5>
              <div className="alert-item" style={{ borderLeftColor: '#F44336', backgroundColor: '#FFF5F5' }}>
                <div className="alert-severity" style={{ color: '#F44336', fontWeight: 'bold' }}>
                  CRITICAL: {criticalPsiValue}
                </div>
                <div className="alert-description" style={{ color: '#333' }}>
                  Linked Sensor ({linkedSensor?.name || linkedSensor?.device_id}) is reporting highly abnormal telemetry. Asset may be experiencing failure.
                </div>
              </div>
            </div>
          )}

          {alerts.length > 0 && (
            <div className="asset-alerts" style={{ marginTop: '10px' }}>
              <h5>
                <i className="fas fa-exclamation-triangle"></i>
                Active Alerts ({alerts.length})
              </h5>
              {alerts.map((alert, index) => (
                <div key={alert.id || index} className="alert-item" style={{ borderLeftColor: getAlertColor(alert.severity) }}>
                  <div className="alert-severity" style={{ color: getAlertColor(alert.severity) }}>
                    {alert.severity?.toUpperCase() || 'MEDIUM'}
                  </div>
                  <div className="alert-description">
                    {alert.description || alert.message || 'No description'}
                  </div>
                  {alert.predictedFailureDate && (
                    <div className="alert-date">
                      Predicted: {new Date(alert.predictedFailureDate).toLocaleDateString()}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </Popup>
    </Polyline>
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.asset.id === nextProps.asset.id &&
    prevProps.asset.status === nextProps.asset.status &&
    prevProps.isSelected === nextProps.isSelected &&
    prevProps.alerts.length === nextProps.alerts.length &&
    prevProps.sensorReading === nextProps.sensorReading &&
    prevProps.showTooltip === nextProps.showTooltip
  );
});

RoadAssetPolyline.displayName = 'RoadAssetPolyline';

// ============================================================================
// SINGLE ASSET MARKER COMPONENT (Standard Points)
// ============================================================================

const AssetMarker = memo(({ 
  asset, 
  isSelected, 
  alerts = [],
  onAssetClick,
  showTooltip = true,
  linkedSensor,
  sensorReading,
  isFloodCritical = false
}) => {
  if (MARKER_DEBUG) assetMarkerDebugger.log('DEBUG', 'asset_marker_render', { // --- UPDATE (v3): dev only
    assetId: asset.id,
    assetName: asset.name,
    hasAlerts: alerts.length > 0,
    isSelected
  });

  const hasAlerts = alerts.length > 0;

  // --- PHASE 4: Evaluate live sensor data to see if Asset is critically failing ---
  let isCascadingCritical = false;
  let criticalPsiValue = null;

  if (sensorReading) {
    const psi = sensorReading.psi ?? sensorReading.pressure ?? sensorReading.level;
    if (psi !== undefined) {
      if (typeof psi === 'number' && (psi > 100 || psi < 20)) {
        isCascadingCritical = true;
        criticalPsiValue = psi;
      } else if (psi === 'EMPTY' || psi === 'HIGH') {
        isCascadingCritical = true;
        criticalPsiValue = psi;
      }
    }
  }

  // Create custom icon
  const icon = useMemo(() => {
    return createAssetIcon(asset, hasAlerts, isSelected, isCascadingCritical, isFloodCritical);
  }, [asset, hasAlerts, isSelected, isCascadingCritical, isFloodCritical]);

  // Handle marker click
  const handleClick = useCallback(() => {
    assetMarkerDebugger.log('INFO', 'asset_clicked', {
      assetId: asset.id,
      assetName: asset.name,
      assetType: asset.type,
      status: asset.status,
      alertCount: alerts.length
    });
    
    if (onAssetClick) {
      onAssetClick(asset);
    }
  }, [asset, alerts.length, onAssetClick]);

  // Validate coordinates
  if (!asset.latitude || !asset.longitude) {
    return null;
  }

  return (
    <Marker
      position={[asset.latitude, asset.longitude]}
      icon={icon}
      eventHandlers={{
        click: handleClick,
        mouseover: () => {
          assetMarkerDebugger.log('DEBUG', 'asset_hover', { assetId: asset.id });
        }
      }}
    >
      {/* Tooltip */}
      {showTooltip && (
        <Tooltip direction="top" offset={[0, -10]} opacity={0.9}>
          <div className="asset-tooltip">
            <strong>{asset.name}</strong>
            <div className="asset-type">{asset.type}</div>
          </div>
        </Tooltip>
      )}

      {/* Popup with detailed info */}
      <Popup maxWidth={300} minWidth={200}>
        <style>{cascadingAnimation}</style>
        <div className="asset-popup">
          <h4>{asset.name}</h4>
          
          <div className="asset-details">
            {/* --- UPDATE (v3): Public Utilities identity */}
            {asset.asset_code && (
              <div className="detail-row">
                <span className="label">Code:</span>
                <span className="value" style={{ fontFamily: 'monospace' }}>{asset.asset_code}</span>
              </div>
            )}
            {asset.constituency && (
              <div className="detail-row">
                <span className="label">Constituency:</span>
                <span className="value">{asset.constituency}</span>
              </div>
            )}
            {/* --- END UPDATE */}
            <div className="detail-row">
              <span className="label">Type:</span>
              <span className="value">{asset.type}</span>
            </div>
            
            <div className="detail-row">
              <span className="label">Status:</span>
              <span 
                className="value" 
                style={{ color: getStatusColor(asset.status) }}
              >
                <i className="fas fa-circle"></i> {asset.status || 'Unknown'}
              </span>
            </div>
            
            {asset.location && (
              <div className="detail-row">
                <span className="label">Location:</span>
                <span className="value">{asset.location}</span>
              </div>
            )}
            
            {asset.installDate && (
              <div className="detail-row">
                <span className="label">Installed:</span>
                <span className="value">
                  {new Date(asset.installDate).toLocaleDateString()}
                </span>
              </div>
            )}
            
            {asset.lastMaintenance && (
              <div className="detail-row">
                <span className="label">Last Maintenance:</span>
                <span className="value">
                  {new Date(asset.lastMaintenance).toLocaleDateString()}
                </span>
              </div>
            )}

            {/* 🚀 PHASE 4: Bridge Structural Details in Popup */}
            {asset.type === 'Bridge' && asset.metadata?.structural_details && (
              <>
                <div className="detail-row" style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #eee' }}>
                  <span className="label">Spans:</span>
                  <span className="value">{asset.metadata.structural_details.number_of_spans || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Foundation:</span>
                  <span className="value">{asset.metadata.structural_details.foundation_type || 'N/A'}</span>
                </div>
              </>
            )}
          </div>

          {/* --- PHASE 4: Inject Live Sensor Alert directly into Asset Popup --- */}
          {isCascadingCritical && (
            <div className="asset-alerts" style={{ marginTop: '15px' }}>
              <h5 style={{ color: '#F44336', fontWeight: 'bold' }}>
                <i className="fas fa-satellite-dish" style={{ marginRight: '5px' }}></i> 
                Sensor Anomaly Detected
              </h5>
              <div className="alert-item" style={{ borderLeftColor: '#F44336', backgroundColor: '#FFF5F5' }}>
                <div className="alert-severity" style={{ color: '#F44336', fontWeight: 'bold' }}>
                  CRITICAL: {criticalPsiValue}
                </div>
                <div className="alert-description" style={{ color: '#333' }}>
                  Linked Sensor ({linkedSensor?.name || linkedSensor?.device_id}) is reporting highly abnormal telemetry. Asset may be experiencing failure.
                </div>
              </div>
            </div>
          )}

          {/* Alerts Section */}
          {alerts.length > 0 && (
            <div className="asset-alerts" style={{ marginTop: '10px' }}>
              <h5>
                <i className="fas fa-exclamation-triangle"></i>
                Active Alerts ({alerts.length})
              </h5>
              {alerts.map((alert, index) => (
                <div 
                  key={alert.id || index} 
                  className="alert-item"
                  style={{ borderLeftColor: getAlertColor(alert.severity) }}
                >
                  <div className="alert-severity" style={{ color: getAlertColor(alert.severity) }}>
                    {alert.severity?.toUpperCase() || 'MEDIUM'}
                  </div>
                  <div className="alert-description">
                    {alert.description || alert.message || 'No description'}
                  </div>
                  {alert.predictedFailureDate && (
                    <div className="alert-date">
                      Predicted: {new Date(alert.predictedFailureDate).toLocaleDateString()}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </Popup>
    </Marker>
  );
}, (prevProps, nextProps) => {
  // Custom comparison for memo optimization
  return (
    prevProps.asset.id === nextProps.asset.id &&
    prevProps.asset.status === nextProps.asset.status &&
    prevProps.asset.lastMaintenance === nextProps.asset.lastMaintenance &&
    prevProps.isSelected === nextProps.isSelected &&
    prevProps.alerts.length === nextProps.alerts.length &&
    prevProps.sensorReading === nextProps.sensorReading && // Must rerender if telemetry changes
    prevProps.isFloodCritical === nextProps.isFloodCritical &&
    prevProps.showTooltip === nextProps.showTooltip
  );
});

AssetMarker.displayName = 'AssetMarker';

// ============================================================================
// MAIN ASSET MARKERS COMPONENT
// ============================================================================

const AssetMarkers = ({
  assets = [],
  selectedAsset = null,
  onAssetClick,
  alertsByAsset = {},
  showAlerts = true,
  showTooltips = true,
  sensors = [], // Passed from Phase 4
  readings = {}, // Passed from Phase 4
  criticalWellIds = new Set()
}) => {
  assetMarkerDebugger.incrementRender();
  assetMarkerDebugger.log('INFO', 'component_render', {
    assetCount: assets.length,
    hasSelectedAsset: !!selectedAsset,
    showAlerts,
    alertAssetCount: Object.keys(alertsByAsset).length
  });

  // Filter out assets with invalid coordinates
  const validAssets = useMemo(() => {
    const valid = assets.filter(asset => {
      // 🚀 NEW LOGIC: Support Roads natively in AssetMarkers
      if (asset.type === 'road' || asset.type === 'street') {
          // 🚀 FIX: Fallback to grab polyline_points (the actual property saved by the new road drawing feature)
          let geoData = asset.polyline_points || asset.path_coordinates || asset.geo_point;
          if (typeof geoData === 'string') {
              try { geoData = JSON.parse(geoData); } catch (e) { geoData = null; }
          }
          
          // Fallback parsing to verify we have line data
          if (Array.isArray(geoData) && geoData.length >= 2) return true;
          if (geoData && geoData.type === 'LineString' && geoData.coordinates?.length >= 2) return true;
          if (asset.start_lat && asset.start_lng && asset.end_lat && asset.end_lng) return true;
          
          return false;
      }

      // Default point geometry logic
      const isValid = asset.latitude && 
                      asset.longitude && 
                      !isNaN(asset.latitude) && 
                      !isNaN(asset.longitude);
      
      if (!isValid && MARKER_DEBUG) { // --- UPDATE (v3): dev only
        assetMarkerDebugger.log('WARNING', 'invalid_asset_coordinates', {
          assetId: asset.id,
          assetName: asset.name,
          latitude: asset.latitude,
          longitude: asset.longitude
        });
      }
      
      return isValid;
    });

    assetMarkerDebugger.log('DEBUG', 'assets_filtered', {
      total: assets.length,
      valid: valid.length,
      invalid: assets.length - valid.length
    });

    return valid;
  }, [assets]);

  // Handle asset click
  const handleAssetClick = useCallback((asset) => {
    assetMarkerDebugger.log('INFO', 'asset_click_handler', {
      assetId: asset.id,
      assetName: asset.name
    });
    
    if (onAssetClick) {
      onAssetClick(asset);
    }
  }, [onAssetClick]);

  // --- UPDATE (v3): one Map of linked sensors (was sensors.find() per asset per render — O(assets × sensors))
  const sensorByAssetId = useMemo(() => {
    const m = new Map();
    for (const s of sensors) {
      if (!s.linked_entity_type || s.linked_entity_type === 'none' || s.linked_entity_type === 'pipeline') continue;
      if (s.linked_entity_id != null && !m.has(String(s.linked_entity_id))) m.set(String(s.linked_entity_id), s);
      if (s.asset_id != null && !m.has(String(s.asset_id))) m.set(String(s.asset_id), s);
    }
    return m;
  }, [sensors]);
  // --- END UPDATE

  if (validAssets.length === 0) {
    assetMarkerDebugger.log('WARNING', 'no_valid_assets_to_display');
    return null;
  }

  return (
    <>
      {validAssets.map(asset => {
        const alerts = showAlerts ? (alertsByAsset[asset.id] || []) : [];
        const isSelected = selectedAsset?.id === asset.id;

        // 🚨 Determine if this well is flagged with high water / flood hazard
        const isFloodCritical = Boolean(criticalWellIds && criticalWellIds.has(String(asset.id)));

        // --- PHASE 4: Find if this asset has a sensor attached (checks linked_entity_id & asset_id), and extract its reading ---
        const linkedSensor = sensorByAssetId.get(String(asset.id)); // --- UPDATE (v3): O(1)
        
        const sensorReading = linkedSensor ? readings[linkedSensor.device_id || linkedSensor.id] : null;

        // 🚀 RENDER ROAD/STREET AS A POLYLINE
        if (asset.type === 'road' || asset.type === 'street') {
          return (
            <RoadAssetPolyline
              key={asset.id}
              asset={asset}
              isSelected={isSelected}
              alerts={alerts}
              onAssetClick={handleAssetClick}
              showTooltip={showTooltips}
              linkedSensor={linkedSensor}
              sensorReading={sensorReading}
            />
          );
        }

        // 🚀 DEFAULT RENDER FOR POINT ASSETS (Wells, Pumps, Valves, etc.)
        return (
          <AssetMarker
            key={asset.id}
            asset={asset}
            isSelected={isSelected}
            alerts={alerts}
            onAssetClick={handleAssetClick}
            showTooltip={showTooltips}
            linkedSensor={linkedSensor}
            sensorReading={sensorReading}
            isFloodCritical={isFloodCritical}
          />
        );
      })}
    </>
  );
};

// ============================================================================
// PROP TYPES
// ============================================================================

AssetMarkers.propTypes = {
  assets: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    latitude: PropTypes.number,
    longitude: PropTypes.number,
    status: PropTypes.string,
    location: PropTypes.string,
    installDate: PropTypes.string,
    lastMaintenance: PropTypes.string
  })),
  selectedAsset: PropTypes.object,
  onAssetClick: PropTypes.func,
  alertsByAsset: PropTypes.object,
  showAlerts: PropTypes.bool,
  showTooltips: PropTypes.bool,
  sensors: PropTypes.array,
  readings: PropTypes.object
};

// Performance optimization with React.memo
export default memo(AssetMarkers, (prevProps, nextProps) => {
  const isEqual =
    prevProps.assets === nextProps.assets &&
    prevProps.selectedAsset?.id === nextProps.selectedAsset?.id &&
    prevProps.alertsByAsset === nextProps.alertsByAsset &&
    prevProps.readings === nextProps.readings &&
    prevProps.criticalWellIds === nextProps.criticalWellIds && // 🚨 Re-render when flood/critical wells change
    prevProps.showAlerts === nextProps.showAlerts &&
    prevProps.showTooltips === nextProps.showTooltips;

  if (!isEqual) {
    assetMarkerDebugger.log('DEBUG', 'component_rerender_triggered', {
      assetsChanged: prevProps.assets !== nextProps.assets,
      selectedAssetChanged: prevProps.selectedAsset?.id !== nextProps.selectedAsset?.id,
      alertsChanged: prevProps.alertsByAsset !== nextProps.alertsByAsset,
      criticalWellIdsChanged: prevProps.criticalWellIds !== nextProps.criticalWellIds
    });
  }

  return isEqual;
});